var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import classNames from 'classnames';
import { max, min } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { getResultClassName } from '../../common/utils';
import { APPROXIMATION_MARGIN, LABEL_MARGIN } from '../../charts/constants';
import AreaChart from './AreaChart';
import LineChart from './LineChart';
import VerticalLine from './VerticalLine';
import TrendlineChartGoal from './TrendlineChartGoal';
import PointChart from './PointChart';
import TrendlineChartTooltip from './TrendLineChartTooltip';
import { UNITS_PERCENTAGE } from '../../common/constants';
export var runIsPercentage = function (data) { return (data.length > 0 && data[data.length - 1].units && data[data.length - 1].units === UNITS_PERCENTAGE || false); };
export var prepareResults = function (data) {
    var isPercentage = runIsPercentage(data);
    return data.map(function (d, i) {
        var result = isPercentage ? d.resultPercentage : d.result;
        return {
            point: [
                d.x ? d.x : i,
                result,
            ],
            className: getResultClassName(d.resultStatus),
        };
    });
};
export var getYMin = function (approximated, data) {
    var isPercentage = runIsPercentage(data);
    var dataMinValue = min(data, function (d) { return isPercentage ? (d.resultPercentage || 0) - APPROXIMATION_MARGIN : d.result - APPROXIMATION_MARGIN; }) || 0;
    return approximated ? dataMinValue : Math.min(dataMinValue, 0);
};
export var getYMax = function (approximated, data) {
    var isPercentage = runIsPercentage(data);
    var dataMaxValue = max(data, function (d) { return isPercentage ? (d.resultPercentage || 0) + APPROXIMATION_MARGIN : d.result + APPROXIMATION_MARGIN; }) || 100;
    if (approximated) {
        return dataMaxValue;
    }
    else {
        var maxResultTotal = max(data, function (d) { return d.resultTotal; }) || 0;
        return isPercentage ? Math.max(dataMaxValue, 100) : maxResultTotal;
    }
};
export var filterThresholdData = function (d) { return d[1]; };
var TrendlineChart = /** @class */ (function (_super) {
    __extends(TrendlineChart, _super);
    function TrendlineChart(props) {
        var _this = _super.call(this, props) || this;
        _this.state = { hovered: undefined, tooltip: null, linePosition: undefined };
        _this.setTooltip = _this.setTooltip.bind(_this);
        _this.unsetTooltip = _this.unsetTooltip.bind(_this);
        return _this;
    }
    TrendlineChart.prototype.setTooltip = function (_a) {
        var id = _a.id, x = _a.x, y = _a.y, size = _a.size;
        var _b = this.props, _c = _b.data, data = _c === void 0 ? [] : _c, approximated = _b.approximated;
        var d = data[id];
        var t = __assign({ position: { x: x + size, y: y + size }, className: getResultClassName(d.resultStatus) }, d);
        var y1 = getYMin(approximated, data);
        var y2 = getYMax(approximated, data);
        this.setState({ tooltip: t, hovered: id, linePosition: { x: x, y1: y1, y2: y2 } });
    };
    TrendlineChart.prototype.unsetTooltip = function () {
        this.setState({ tooltip: null, hovered: undefined, linePosition: undefined });
    };
    TrendlineChart.prototype.render = function () {
        var _this = this;
        var _a = this.props, _b = _a.chartId, chartId = _b === void 0 ? 'trendlineChart' : _b, _c = _a.width, width = _c === void 0 ? 800 : _c, _d = _a.height, height = _d === void 0 ? 300 : _d, _e = _a.data, data = _e === void 0 ? [] : _e, approximated = _a.approximated, xAxisLabel = _a.xAxisLabel, yAxisLabel = _a.yAxisLabel, showThresholdAreas = _a.showThresholdAreas;
        var _f = this.state, hovered = _f.hovered, tooltip = _f.tooltip, linePosition = _f.linePosition;
        var passedThresholdData = data.map(function (d, i) { return ([d.x ? d.x : i, d.thresholds !== undefined ? d.thresholds.pass : 0]); });
        var warningThresholdData = data.map(function (d, i) { return ([d.x ? d.x : i, d.thresholds !== undefined ? d.thresholds.warn : 0]); });
        /* For better visibility, if there is change from result without warning threshold
         and result with warning thresholds, then the warning threshold area
         should be visible since previous result (without warning threshold) */
        var warningThresholdAreaData = warningThresholdData.map(function (d, i) {
            var first, second;
            first = d[0];
            second = d[1];
            if (second === undefined) {
                if (warningThresholdData[i + 1] !== undefined && warningThresholdData[i + 1][1] !== undefined) {
                    second = warningThresholdData[i + 1][1];
                }
                else {
                    second = undefined;
                }
            }
            return [first, second];
        });
        var yMin = getYMin(approximated, data);
        var yMax = getYMax(approximated, data);
        var yRange = [yMin, yMax];
        var accessor = function (d, i) { return (d.x ? d.x : i); };
        var xMin = min(data, accessor) || 0;
        var xMax = max(data, accessor) || 100;
        var xRange = [xMin, xMax];
        var scaleX = scaleLinear().domain(xRange).rangeRound([0, width]);
        var scaleY = scaleLinear().domain(yRange).range([height, 0]);
        return (React.createElement("div", { className: "trendlineChart" },
            React.createElement("svg", { id: chartId, width: width, height: height },
                showThresholdAreas && [
                    React.createElement(AreaChart, { key: "trendlineChart__passedArea", chartId: "trendlineChart__passedArea", scaleX: scaleX, scaleY: scaleY, height: height, width: width, data: passedThresholdData }),
                    React.createElement(AreaChart, { key: "trendlineChart__warningArea", chartId: "trendlineChart__warningArea", scaleX: scaleX, scaleY: scaleY, height: height, width: width, data: warningThresholdAreaData }),
                    React.createElement(LineChart, { key: "trendlineChart__warningLine", chartId: "trendlineChart__warningLine", data: warningThresholdAreaData.map(function (w) { return ({ point: w }); }), width: width, height: height, scaleX: scaleX, scaleY: scaleY }),
                    React.createElement(LineChart, { key: "trendlineChart__passedLine", chartId: "trendlineChart__passedLine", data: passedThresholdData.map(function (w) { return ({ point: w }); }), width: width, height: height, scaleX: scaleX, scaleY: scaleY }),
                ],
                React.createElement(LineChart, { chartId: "trendlineChart__valueLines", data: prepareResults(data), width: width, height: height, scaleX: scaleX, scaleY: scaleY }),
                React.createElement("text", { x: LABEL_MARGIN, y: scaleY(yMin) + (1.5 * LABEL_MARGIN), transform: "rotate(-90, 0, " + scaleY(yMin) + ")", className: "trendlineChart__label" }, yAxisLabel),
                React.createElement("text", { x: width - LABEL_MARGIN, textAnchor: "end", y: scaleY(yMin) - LABEL_MARGIN, className: "trendlineChart__label" }, xAxisLabel),
                showThresholdAreas &&
                    React.createElement(VerticalLine, { position: linePosition, scaleY: scaleY, className: classNames('trendlineChart__tooltipLine', { hidden: !linePosition }) }),
                React.createElement(TrendlineChartGoal, { scaleX: scaleX, scaleY: scaleY, yMin: yMin, yMax: yMax, xMin: 0, xMax: width, data: data }),
                showThresholdAreas && [
                    // passed threshold points
                    React.createElement(PointChart, { chartId: "trendlineChart__passedThresholdPointChart", key: "trendlineChart__passedThresholdPointChart", scaleX: scaleX, scaleY: scaleY, height: height, pointSize: 2, width: width, data: passedThresholdData.map(function (w) { return ({ point: w }); }), hovered: hovered, pointClassName: "passed noFill noStroke" }),
                    // warning threshold points
                    React.createElement(PointChart, { chartId: "trendlineChart__warningThresholdPointChart", key: "trendlineChart__warningThresholdPointChart", scaleX: scaleX, scaleY: scaleY, height: height, pointSize: 2, width: width, data: warningThresholdData.map(function (w) { return ({ point: w }); }), hovered: hovered, pointClassName: "warning noFill noStroke" }),
                ],
                React.createElement(PointChart, { chartId: "trendlineChart__values", data: prepareResults(data), scaleX: scaleX, scaleY: scaleY, height: height, width: width, hovered: hovered, onMouseOver: function (props) { return _this.setTooltip(props); }, onMouseOut: function () { return _this.unsetTooltip(); }, pointSize: 4 })),
            React.createElement(TrendlineChartTooltip, __assign({ className: tooltip ? undefined : 'hidden' }, tooltip))));
    };
    return TrendlineChart;
}(React.Component));
export default TrendlineChart;
//# sourceMappingURL=TrendlineChart.js.map